import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import {
  DiagnosisStatus,
  DiagnosisFeedback,
  TicketAiDiagnosis
} from 'src/interfaces/TicketTypes';

interface AiDiagnosisPanelProps {
  diagnosis: TicketAiDiagnosis | null;
  onRefresh: () => void;
}

function getConfidenceColor(
  confidence: number
): 'success' | 'warning' | 'error' {
  if (confidence >= 0.7) return 'success';
  if (confidence >= 0.4) return 'warning';
  return 'error';
}

function parseActions(actionsJson: string | null): string[] {
  if (!actionsJson) return [];
  try {
    const parsed = JSON.parse(actionsJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return actionsJson.split('\n').filter((s) => s.trim());
  }
}

const feedbackLabels: Record<number, string> = {
  [DiagnosisFeedback.Accepted]: 'Accepted',
  [DiagnosisFeedback.Rejected]: 'Rejected',
  [DiagnosisFeedback.Overridden]: 'Overridden'
};

function AiDiagnosisPanel({ diagnosis, onRefresh }: AiDiagnosisPanelProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const isPending =
    diagnosis &&
    (diagnosis.status === DiagnosisStatus.Pending ||
      diagnosis.status === DiagnosisStatus.Analyzing);

  useEffect(() => {
    if (isPending) {
      intervalRef.current = setInterval(onRefresh, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPending, onRefresh]);

  const handleFeedback = (feedback: DiagnosisFeedback) => {
    if (!diagnosis) return;
    setSubmittingFeedback(true);
    axiosConfig
      .post('/SubmitDiagnosisFeedback', null, {
        params: {
          ticketId: diagnosis.ticketId,
          feedback
        }
      })
      .then(() => onRefresh())
      .finally(() => setSubmittingFeedback(false));
  };

  if (!diagnosis) {
    return (
      <Card>
        <CardHeader
          title={
            <FormattedMessage
              id="aiDiagnosis"
              defaultMessage="AI Diagnosis"
            />
          }
        />
        <Divider />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="noDiagnosis"
              defaultMessage="No AI diagnosis available."
            />
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (isPending) {
    return (
      <Card>
        <CardHeader
          title={
            <FormattedMessage
              id="aiDiagnosis"
              defaultMessage="AI Diagnosis"
            />
          }
        />
        <Divider />
        <CardContent>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2">
              <FormattedMessage
                id="diagnosisAnalyzing"
                defaultMessage="AI is analyzing this ticket..."
              />
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (diagnosis.status === DiagnosisStatus.Failed) {
    return (
      <Card>
        <CardHeader
          title={
            <FormattedMessage
              id="aiDiagnosis"
              defaultMessage="AI Diagnosis"
            />
          }
        />
        <Divider />
        <CardContent>
          <Alert severity="error">
            <FormattedMessage
              id="diagnosisFailed"
              defaultMessage="AI diagnosis failed. Please try again later."
            />
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const actions = parseActions(diagnosis.recommendedActions);
  const hasFeedback = diagnosis.feedback != null;

  return (
    <Card>
      <CardHeader
        title={
          <FormattedMessage id="aiDiagnosis" defaultMessage="AI Diagnosis" />
        }
      />
      <Divider />
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            <FormattedMessage id="rootCause" defaultMessage="Root Cause" />
          </Typography>
          <Typography variant="body2">
            {diagnosis.rootCause ?? '-'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            <FormattedMessage id="confidence" defaultMessage="Confidence" />
          </Typography>
          {diagnosis.confidence != null ? (
            <Chip
              label={`${Math.round(diagnosis.confidence * 100)}%`}
              color={getConfidenceColor(diagnosis.confidence)}
              size="small"
            />
          ) : (
            <Typography variant="body2">-</Typography>
          )}
        </Box>

        {actions.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              <FormattedMessage
                id="recommendedActions"
                defaultMessage="Recommended Actions"
              />
            </Typography>
            <List dense disablePadding>
              {actions.map((action, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      color="primary"
                    />
                  </ListItemIcon>
                  <ListItemText primary={action} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Divider />

        {hasFeedback ? (
          <Box>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                id="feedbackSubmitted"
                defaultMessage="Feedback: {feedback}"
                values={{
                  feedback: feedbackLabels[diagnosis.feedback!] ?? 'Unknown'
                }}
              />
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle2">
              <FormattedMessage
                id="diagnosisFeedbackLabel"
                defaultMessage="Was this helpful?"
              />
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<ThumbUpIcon />}
              disabled={submittingFeedback}
              onClick={() => handleFeedback(DiagnosisFeedback.Accepted)}
            >
              <FormattedMessage id="accept" defaultMessage="Accept" />
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<ThumbDownIcon />}
              disabled={submittingFeedback}
              onClick={() => handleFeedback(DiagnosisFeedback.Rejected)}
            >
              <FormattedMessage id="reject" defaultMessage="Reject" />
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default AiDiagnosisPanel;
